import * as Sentry from '@sentry/browser';
import { getShopifyIdFromBase64, sanitizeCmsImage } from './shopify-helpers';
import { format } from '@shopify/dates';

const chunkifyArray = ({ inputArray, perChunk }) => {
	return inputArray.reduce((resultArray, item, index) => {
		const chunkIndex = Math.floor(index / perChunk);

		if (!resultArray[chunkIndex]) {
			resultArray[chunkIndex] = [];
		}

		resultArray[chunkIndex].push(item);

		return resultArray;
	}, []);
};

/**
 * Flattens 'edges' and 'node' options in a graphQL response
 *
 * @param {*} graphQLEdges
 * @return {nodeList}
 */
const extractGraphQLNodes = graphQLEdges => {
	let nodeList = [];

	for (let i = 0; i < graphQLEdges.length; i++) {
		let { node } = graphQLEdges[i];

		try {
			const childToExpand = Object.keys(node).find(k => typeof node[k] === 'object' && Object.keys(node[k])[0] === 'edges');

			if (childToExpand) {
				node = extractGraphQLNodes(node[childToExpand].edges);
			}
		} catch (error) {}

		nodeList.push(node);
	}

	return nodeList;
};

/**
 * Generates tuple for each product in a specified collection and its variant that matches the current base frame type.
 * Any time a variant is not found, the map function returns 'null' which is filtered out; this prevents any unlisted or inactive products
 * from being rendered and protects the data structure.
 *
 * Important:
 * - product ID must be converted from base64 version
 * - price must be converted from string to number (uses Bitwise operator "~~")
 *
 * Example: if you wanted the Kirby variant of 'The Galaxy' in the 'Classic Designs' collection: ['The Galaxy', {...kirbyVariant}]
 *
 * @param {array} products
 * @returns [string, object]
 */
//Note - Leave for when we migrate to Headless (updated: 06/28/2022)
const generateTuple = (_Frame, products) => {
	const findByTitleAndSpread = arr => {
		const foundObject = arr.find(({ node }) => _Frame.includes(node.title));

		if (!foundObject) return null;
		return { ...foundObject.node, id: getShopifyIdFromBase64(foundObject.node.id), price: ~~foundObject.node.price };
	};

	return products
		.map(
			({
				node: {
					title: prodTitle,
					variants: { edges },
				},
			}) => {
				const foundVariant = findByTitleAndSpread(edges);

				if (!foundVariant) return null;
				return [prodTitle, foundVariant];
			}
		)
		.filter(Boolean);
};

//   Note - Leave for when we migrate to Headless (updated: 06/28/2022)
const generateGlowTuple = (_Frame, products) => {
	const findByTitleAndSpread = arr => {
		const foundObjectIndex = arr.findIndex(({ node }) => _Frame.includes(node.title));
		const foundObject = arr[foundObjectIndex];
		if (!(foundObjectIndex + 1)) return null;
		return {
			...foundObject.node,
			id: getShopifyIdFromBase64(foundObject.node.id),
			price: ~~foundObject.node.price,
			variantIndex: foundObjectIndex,
		};
	};

	return products
		.map(
			({
				node: {
					title: prodTitle,
					images: { edges: imageEdges },
					variants: { edges: variantEdges },
				},
			}) => {
				const foundVariant = findByTitleAndSpread(variantEdges);
				const chunkifiedVariantImages = chunkifyArray({ inputArray: extractGraphQLNodes(imageEdges), perChunk: 3 });

				const dataForTuple = {
					...foundVariant,
					...{ variantImages: chunkifiedVariantImages[foundVariant.variantIndex] },
				};

				if (!foundVariant) return null;
				return [prodTitle, dataForTuple];
			}
		)
		.filter(Boolean);
};

const assetExtensionRegex = /\.(png|gif|webp|jpg|jpeg|pjpg|tiff|heif|avif|bmp|svg)/gim;

/**
 * Generates a src attribute for an image HTML element based off Shopify's CDN URL
 * and a custom size.
 *
 * Returns a string of concatenated new urls with the sies added on.
 *
 * @param {string} url
 * @param {number} size
 * @returns {string}
 */
const generateImgSrc = (url, size) => {
	const sanitizedUrl = sanitizeCmsImage(url);
	const ext = sanitizedUrl.match(assetExtensionRegex);

	return sanitizedUrl.replace(assetExtensionRegex, `_${size}x${ext[0]}`);
};

const formatDate = date => {
	if (!date) return null;
	const newDate = new Date(date);
	return format(newDate, 'MMMM DD, YYYY • h:mm a');
};

/**
 * Generates an srcset attribute for image HTML elements based off Shopify's default URL
 * provided on the product object and a custom "sizes" array.
 *
 * Returns a string of concatenated new urls with the sies added on.
 *
 * @param {string} url
 * @param {array} sizes
 * @returns {string}
 */
const generateSrcSet = (url, sizes) => {
	const sanitizedUrl = sanitizeCmsImage(url);
	const ext = sanitizedUrl.match(assetExtensionRegex);

	return sizes.reduce((accumulator, size, index) => {
		const newUrl = sanitizedUrl.replace(assetExtensionRegex, `_${size}x${ext[0]}`);

		return accumulator + (index === 0 ? `${newUrl} ${size}w` : `, ${newUrl} ${size}w`);
	}, '');
};

const generateRandomNum = (min, max) => {
	return Math.random() * (max - min + 0.5) + min;
};

export {
	chunkifyArray,
	generateTuple,
	generateGlowTuple,
	generateImgSrc,
	generateSrcSet,
	extractGraphQLNodes,
	formatDate,
	generateRandomNum,
};
